import {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  useNavigate,
} from 'react-router';
import i18n from 'i18next';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  uiActions,
} from 'web-actions';
import { selectClientsLimitReached } from 'web-selectors';
import {
  Modal,
  Button,
  Select,
  Avatar,
  Spinner,
  Tooltip,
  NotFound,
  Checkbox,
  ContentWrapper,
} from 'web-components';
import {
  Flex,
  Text,
  icons,
} from 'web-styled';

import * as S from './styled';
import Header from '../Header';

const tooltipTexts = {
  restore: 'Restore a client using this button',
};
const mapState = (state => ({
  selectedIds: coreSelectors.getSelectedIds(
    'archivedClients',
  )(state),
  archivedClients: coreSelectors.sortedSelectionResourceList(
    'archivedClients',
  )(state),
  searchOptions: coreSelectors.getSearchOptions(
    'archivedClients',
  )(state),
  readStatus: getStatus(
    state,
    [
      'archivedClients.requests.fetchArchivedClients.status',
    ],
    true,
  ),
  isClientsLimitReached: selectClientsLimitReached(state),
}));

type Props = {
  onChecked: { (data: any): void };
};

const Archived = ({ onChecked }: Props) => {
  const navigator = useNavigate();
  const dispatch = useDispatch();
  const {
    readStatus,
    selectedIds,
    searchOptions,
    archivedClients,
    isClientsLimitReached,
  } = useSelector(mapState, shallowEqual);

  useEffect(() => {
    dispatch(
      coreActions.requestFetchArchivedClients({
        // qs: {
        //   page: 1,
        //   per_page: 50,
        // },
      }),
    );
  }, []);

  const selectedClients = useMemo(
    () => archivedClients.filter(c => c.selected),
    [archivedClients],
  )

  const restore = (cls: any[]) => {
    const onConfirmAction = coreActions.requestRestoreClients(cls);
    const multiple = cls.length > 1;
    const bodyText = i18n.t(
      `client.unarchive${multiple ? '_multiple' : ''}_modal`,
    );

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: `Restore Client${multiple ? 's' : ''}`,
          bodyText,
          confirmText: 'Confirm',
        },
        onConfirmAction,
      }),
    );
  };

  const onRequestRestore = useCallback((cls: any[]) => {
    if (isClientsLimitReached) {
      dispatch(
        uiActions.setUiState({
          addClientModal: true,
        }),
      )
    } else {
      restore(cls);
    }
  }, [isClientsLimitReached]);

  const handleDeleteItems = () => {
    const onSuccess = () => {
      dispatch(
        coreActions.deleteResources({
          resourceType: 'archivedClients',
          resources: selectedClients,
        }),
      );
    };
    const onConfirmAction = coreActions.requestDeleteClientItems({
      entityType: 'clients',
      resourceType: 'archivedClients',
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete client(s)',
          bodyText: 'Are you sure you want to delete selected client(s)?',
          confirmText: 'Delete',
        },
        onSuccess,
        onConfirmAction,
      }),
    );
  };

  const handleDeleteClient = (client) => {
    const fullName = `${client.user_profile.first_name} ${client.user_profile.last_name}`;
    const onSuccess = () => {
      dispatch(
        coreActions.deleteResources({
          resourceType: 'archivedClients',
          resources: [{ id: client.id }],
        }),
      );
    };
    const onConfirmAction = coreActions.requestDeleteClientEntity({
      id: client.id,
      resourceType: 'clients',
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete User',
          bodyText: `Are you sure you want to delete <b>${fullName}</b>?`,
          confirmText: 'Delete',
        },
        onSuccess,
        onConfirmAction,
      }),
    );
  };

  return (
    <ContentWrapper
      header={(
        <Header
          items={archivedClients}
          resourceType="archivedClients"
          onRequestRestore={() => onRequestRestore(selectedClients)}
          onRequestDeleteItems={handleDeleteItems}
        />
      )}
    >
      <Flex
        column
        fillSpace
        width="100%"
      >
        <S.Search>
          <Select
            value=""
            isClearable
            options={searchOptions}
            placeholder={i18n.t('client.search.placeholder')}
            onChange={(item) => {
              if (item?.value) {
                navigator(`/users/${item.value}/programs`);
              }
            }}
          />
        </S.Search>
        <S.ClientsHeader>
          <Text
            size="18px"
            color="#9ea0a1"
            weight={500}
          >
            {i18n.t('client.archived.title')}
          </Text>
        </S.ClientsHeader>

        {readStatus.pending && (
          <Spinner />
        )}

        {readStatus.failed && (
          <NotFound title="Something is wrong" />
        )}

        {readStatus.succeeded && (
          <>
            {archivedClients.length
              ? archivedClients.map(client => (
                <S.Item key={client.id}>
                  <Flex
                    fillSpace
                    align="center"
                    justify="space-between"
                  >
                    <Flex
                      spacing={10}
                      align="center"
                    >
                      <Checkbox
                        checked={selectedIds.includes(client.id)}
                        onChange={() => onChecked(
                          {
                            id: client.id,
                            selectedIds,
                            resourceType: 'archivedClients',
                          },
                        )}
                      />
                      <Flex
                        align="center"
                        spacing={10}
                      >
                        <Avatar
                          width="28px"
                          height="28px"
                          src={client.user_profile?.avatar?.thumb?.url}
                        />
                        <S.Name>
                          {`${client.user_profile.first_name} ${client.user_profile.last_name}`}
                        </S.Name>
                      </Flex>
                    </Flex>
                    <S.MoveBar>
                      <Flex
                        spacing={10}
                        align="center"
                      >
                        <Tooltip
                          body={tooltipTexts.restore}
                          positions={['left']}
                        >
                          <Button
                            ghost
                            appearance="primary"
                            onClick={() => onRequestRestore([client])}
                          >
                            Restore
                          </Button>
                        </Tooltip>
                        <Button
                          ghost
                          appearance="danger"
                          onClick={() => handleDeleteClient(client)}
                        >
                          Delete
                        </Button>
                      </Flex>
                    </S.MoveBar>
                  </Flex>
                </S.Item>
              )) : (
                <S.Empty>
                  <img src={icons.folder} alt="" />
                  <span>
                    You dont have
                    <br />
                    anything inside this folder.
                  </span>
                </S.Empty>
              )}
          </>
        )}
      </Flex>
    </ContentWrapper>
  );
};

export default Archived;
